// Spawn placement and respawns (§2.12, §4.3, §18.7), re-exported by world.ts. Imports the map
// registry but never the mode registry, so mode modules can respawn beans without a cycle.
import { NO_SLOT, TUNE, VOID } from '../constants';
import { EV, emit } from '../events';
import { BS } from '../ids';
import { MAPS } from '../maps/index';
import { randInt } from '../rng';
import type { Vec2, World } from '../types';
import { hasBody, resetBean } from './bean';
import { datan2 } from './dmath';

/** Places a fresh-life bean standing at (x, y), facing the map center. */
export function spawnBean(w: World, slot: number, x: number, y: number): void {
  const b = w.beans[slot];
  const map = MAPS[w.cfg.mapId];
  resetBean(b);
  b.pos.x = x;
  b.pos.y = y;
  const g = map.groundAt(w, x, y);
  b.z = b.groundZ = g === VOID ? 0 : g;
  const dx = map.center.x - x;
  const dy = map.center.y - y;
  if (dx !== 0 || dy !== 0) b.facing = datan2(dy, dx);
}

/**
 * Brings `slot` back for a new life at `at` (default: `pickSpawn(w, true)`). 'parachute' starts
 * the RESPAWN descent `TUNE.respawn.paraZ` above the ground (invulnerability starts on landing);
 * 'instant' stands the bean up immediately with invulnerability. Server-only.
 */
export function respawnBean(w: World, slot: number, kind: 'parachute' | 'instant', at?: Vec2): void {
  const p = at ?? pickSpawn(w, true);
  spawnBean(w, slot, p.x, p.y);
  const b = w.beans[slot];
  if (kind === 'parachute') {
    b.state = BS.RESPAWN;
    b.z = b.groundZ + TUNE.respawn.paraZ;
  } else {
    b.invulnTicks = TUNE.respawn.invulnTicks;
  }
  emit(w, EV.RESPAWN, slot, NO_SLOT, 0, 0, p.x, p.y);
}

/**
 * A spawn point on solid ground. With `avoidOthers`, the one farthest from every bean that has a
 * body (ties: lowest index); otherwise a seeded random one. Falls back to the map center when no
 * spawn has ground. Server-only: consumes world RNG when `avoidOthers` is false.
 */
export function pickSpawn(w: World, avoidOthers: boolean): Vec2 {
  const map = MAPS[w.cfg.mapId];
  let best = -1;
  let bestScore = -Infinity;
  let valid = 0;
  for (let i = 0; i < map.spawns.length; i++) {
    const s = map.spawns[i];
    if (map.groundAt(w, s.x, s.y) === VOID) continue;
    valid++;
    if (!avoidOthers) continue;
    let nearest = Infinity;
    for (const b of w.beans) if (hasBody(b)) nearest = Math.min(nearest, Math.hypot(b.pos.x - s.x, b.pos.y - s.y));
    if (nearest > bestScore) {
      bestScore = nearest;
      best = i;
    }
  }
  if (valid === 0) return { x: map.center.x, y: map.center.y };
  if (!avoidOthers) {
    let k = randInt(w, valid);
    for (let i = 0; i < map.spawns.length && best < 0; i++) {
      const s = map.spawns[i];
      if (map.groundAt(w, s.x, s.y) !== VOID && k-- === 0) best = i;
    }
  }
  return { x: map.spawns[best].x, y: map.spawns[best].y };
}
